import React from 'react';
import { fmtNum } from '../../utils/formatArcaNumbers';

interface RetrospectiveCardProps {
	/** Importe a traspasar (saldo × %), sobre el que se calcula la retrospectiva. */
	transferredAmount: number;
	/** Rentabilidad anualizada (%) del plan Arca en el periodo comparado. */
	arcaReturnPct: number;
	/** Rentabilidad anualizada (%) del plan de origen en el MISMO periodo. */
	originReturnPct: number;
	/** Periodo (años) de las rentabilidades comparadas — el mismo para ambos planes. */
	returnPeriodYears: number;
	translate: (key: string) => string;
}

// Capitaliza `amount` a la rentabilidad anualizada dada durante `years` años. La rentabilidad
// ya es NETA (DGSFP), así que no se descuenta comisión.
const accrued = (amount: number, returnPct: number, years: number) =>
	amount * (1 + returnPct / 100) ** years;

/**
 * "Los últimos {N} años · ¿Y si ya lo hubieras cambiado?": lo que el importe traspasado habría
 * ganado de más en el plan elegido frente a quedarse en el actual, en el MISMO periodo que la
 * comparación de la gráfica (el más largo que ambos planes tienen). Se pinta bajo la gráfica.
 */
const RetrospectiveCard: React.FC<RetrospectiveCardProps> = ({
	transferredAmount,
	arcaReturnPct,
	originReturnPct,
	returnPeriodYears,
	translate
}) => {
	const fmtPct = (n: number) => `${fmtNum(n, 2)}%`;

	const retroDiff =
		accrued(transferredAmount, arcaReturnPct, returnPeriodYears) -
		accrued(transferredAmount, originReturnPct, returnPeriodYears);
	// El veredicto (quién rindió más) depende de la RENTABILIDAD, no del importe: con saldo 0 el
	// importe sería 0 pero eso no significa que el plan actual haya rendido más. Sin saldo la
	// retrospectiva monetaria no aplica → mensaje neutro con las rentabilidades.
	const hasBalance = transferredAmount > 0;
	const arcaReturnsMore = arcaReturnPct > originReturnPct;
	const arcaWins = arcaReturnsMore && hasBalance;
	const yearsLabel = String(returnPeriodYears);

	return (
		<div className="surface-card bg-primary/5 border border-primary/20 p-4 space-y-2">
			<div>
				<p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
					{(
						translate('individual.comparison.result.eyebrow') || 'Los últimos {years} años'
					).replace('{years}', yearsLabel)}
				</p>
				<h3 className="text-base font-bold">
					{translate('individual.comparison.result.question') || '¿Y si ya lo hubieras cambiado?'}
				</h3>
			</div>
			{!hasBalance ? (
				<p className="text-sm text-muted-foreground leading-snug">
					{(
						translate('individual.comparison.result.noBalance') ||
						'Sin saldo que traspasar no hay ganancia acumulada que mostrar. En rentabilidad neta, este plan ha rendido {arcaReturn} frente al {originReturn} de tu plan actual.'
					)
						.replace('{arcaReturn}', fmtPct(arcaReturnPct))
						.replace('{originReturn}', fmtPct(originReturnPct))}
				</p>
			) : arcaWins ? (
				<p className="text-sm text-muted-foreground leading-snug">
					{(
						translate('individual.comparison.result.caption') ||
						'Si hubieras movido {amount} a este plan hace {years} años, hoy tendrías'
					)
						.replace('{amount}', `${fmtNum(transferredAmount, 0)} €`)
						.replace('{years}', yearsLabel)}{' '}
					<span className="text-xl font-bold font-heading text-primary align-middle">
						+{fmtNum(retroDiff, 0)} €
					</span>{' '}
					{translate('individual.comparison.result.more') || 'más que dejándolo en tu plan actual.'}
				</p>
			) : (
				<p className="text-sm text-muted-foreground leading-snug">
					{(
						translate('individual.insight.yoursBetterRetro') ||
						'Tu plan actual ha rendido más estos {years} años ({originReturn} frente a {arcaReturn} neto). El cambio aquí sería más por diversificar que por rentabilidad.'
					)
						.replace('{years}', yearsLabel)
						.replace('{arcaReturn}', fmtPct(arcaReturnPct))
						.replace('{originReturn}', fmtPct(originReturnPct))}
				</p>
			)}
		</div>
	);
};

export default RetrospectiveCard;
